<template>
  <b-container>
    <b-alert class="mt-2" :show="!!saveError" variant="danger" dismissible fade>
      <b-container>
        <h4 class="alert-heading">{{ $t("errorOccurred") }}</h4>
        <span style="white-space: pre-wrap;">{{ detailedErrorMessage.join("\n") }}</span>
      </b-container>
    </b-alert>

    <ValidationObserver tag="form" ref="form">
      <b-row>
        <b-col class="my-3">
          <router-link to="/" v-html="$t('backToOverview')" />
        </b-col>
      </b-row>
      <b-row align-h="between">
        <b-col>
          <h3>
            {{ $t("general.reference") }}
            {{
            reference.referenceNumber ? `#${reference.referenceNumber}` : null
            }}
          </h3>
          <p
            class="released-information"
            v-if="isAdmin && reference.released && reference.releasedBy"
          >
            <i>
              {{
              $t("refernceRealesedBy", {
              name: reference.releasedBy.name,
              date: new Date(reference.releasedBy.timestamp).toLocaleString(
              $i18n.locale,
              {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              }
              ),
              })
              }}
            </i>
          </p>
        </b-col>

        <b-col align-v="center" align-h="end" cols="auto" class="d-flex align-items-center">
          <Rating :rating="quality" />
        </b-col>
      </b-row>
      <b-row align-h="start">
        <b-col>
          <TagMultiSelect
            style="position: relative; left: -.5rem;"
            v-model="availableLanguages"
            :options="languageOptions"
            @option-click="currentLanguageCode = $event"
          />
        </b-col>
      </b-row>

      <b-tabs class="mt-3">
        <ValidationObserver v-slot="{ errors }">
          <b-tab :title="$t('generalTab')" :title-item-class="{ invalid: countErrors(errors) > 0 }">
            <b-row>
              <b-col>
                <ValidationProvider slim rules="sl!:de,required|ml:max:100" v-slot="{ errors }">
                  <b-form-group
                    label-cols-md="3"
                    label-cols-lg="2"
                    class="mt-4"
                    :label="$t('attributes.title') + '*'"
                  >
                    <MultilangInput
                      :languages="availableLanguages"
                      v-model="reference.title"
                      :langProps="generateLangState(errors[0])"
                    />
                    <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ValidationProvider slim rules="ml:max:100" v-slot="{ errors }">
                  <b-form-group
                    label-cols-md="3"
                    label-cols-lg="2"
                    :label="$t('attributes.subTitle')"
                  >
                    <MultilangInput
                      :languages="availableLanguages"
                      v-model="reference.subTitle"
                      :langProps="generateLangState(errors[0])"
                    />
                    <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <ValidationProvider slim rules="required" v-slot="{ errors, valid }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.location') + '*'"
              >
                <LocationInput
                  v-model="reference.location"
                  :state="errors[0] ? false : valid ? undefined : null"
                />
              </b-form-group>
            </ValidationProvider>
            <b-form-group label-cols-md="3" label-cols-lg="2" :label="$t('attributes.description')">
              <MultilangInput
                :languages="availableLanguages"
                v-model="reference.description"
                v-slot="{ value, input }"
              >
                <RichTextInput :value="value" @input="input($event)" />
              </MultilangInput>
            </b-form-group>

            <ValidationProvider slim rules="max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.eeposContact')"
              >
                <b-form-input
                  v-model="reference.eeposContact"
                  :state="errors[0] ? false : undefined"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <ValidationProvider slim rules="sl!:de,required|ml:max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.industry') + '*'"
              >
                <MultilangInput
                  :languages="availableLanguages"
                  :langProps="generateLangState(errors[0])"
                  v-model="reference.industry"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <ValidationProvider slim rules="ml:max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.clientApplication')"
              >
                <MultilangInput
                  :languages="availableLanguages"
                  v-model="reference.clientApplication"
                  :langProps="generateLangState(errors[0])"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <ValidationProvider slim rules="required" v-slot="{ errors, valid }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.year') + '*'"
              >
                <b-form-select
                  :state="errors[0] ? false : valid ? undefined : null"
                  v-model="reference.year"
                  :options="getYearsUpToNow"
                />
              </b-form-group>
            </ValidationProvider>
          </b-tab>
        </ValidationObserver>

        <ValidationObserver v-slot="{ errors }">
          <b-tab :title="$t('projectTab')" :title-item-class="{ invalid: countErrors(errors) > 0 }">
            <ValidationProvider slim rules="max:100" v-slot="{ errors }">
              <b-form-group
                class="mt-3"
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.projectNumber')"
              >
                <b-form-input
                  v-model="reference.projectNumber"
                  :state="errors[0] ? false : undefined"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <ValidationProvider slim rules="required" v-slot="{ errors, valid }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.productFamily') + '*'"
              >
                <b-form-checkbox-group
                  :state="errors[0] ? false : valid ? undefined : null"
                  class="d-flex justify-content-between mt-2"
                  v-model="reference.productFamily"
                  :options="productFamilies"
                />
              </b-form-group>
            </ValidationProvider>

            <ValidationProvider slim rules="ml:max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                class="mt-4"
                :label="$t('attributes.productScope')"
              >
                <MultilangInput
                  :languages="availableLanguages"
                  v-model="reference.productScope"
                  :langProps="generateLangState(errors[0])"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <b-form-group
              label-cols-md="3"
              label-cols-lg="2"
              :label="$t('attributes.additionalInformation')"
            >
              <MultilangInput
                :languages="availableLanguages"
                v-model="reference.additionalInformation"
                v-slot="{ value, input }"
              >
                <RichTextInput :value="value" @input="input($event)" />
              </MultilangInput>
            </b-form-group>

            <b-form-group label-cols-md="3" label-cols-lg="2" :label="$t('attributes.advantages')">
              <MultilangInput
                :languages="availableLanguages"
                v-model="reference.advantages"
                v-slot="{ value, input }"
              >
                <RichTextInput :value="value" @input="input($event)" />
              </MultilangInput>
            </b-form-group>

            <ValidationProvider slim rules="max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.integrator')"
              >
                <b-form-input
                  v-model="reference.integrator"
                  :state="errors[0] ? false : undefined"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <b-form-group
              label-cols-md="3"
              label-cols-lg="2"
              :label="$t('attributes.projectVolume')"
            >
              <b-input-group>
                <b-form-input
                  type="number"
                  :value="
                    reference.projectVolume
                      ? reference.projectVolume.value
                      : null
                  "
                  @input="
                    reference.projectVolume =
                      $event != ''
                        ? { value: Number($event), currency: 'EUR' }
                        : null
                  "
                />
                <template v-slot:append>
                  <b-input-group-text>EUR</b-input-group-text>
                </template>
              </b-input-group>
            </b-form-group>
          </b-tab>
        </ValidationObserver>

        <ValidationObserver v-slot="{ errors }">
          <b-tab
            :title="$t('customerTab')"
            :title-item-class="{ invalid: countErrors(errors) > 0 }"
          >
            <ValidationProvider slim rules="required|max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                class="mt-4"
                :label="$t('attributes.customer') + '*'"
              >
                <b-form-input
                  :state="errors[0] ? false : undefined"
                  v-model="reference.customerName"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <ValidationProvider slim rules="max:100" v-slot="{ errors }">
              <b-form-group
                label-cols-md="3"
                label-cols-lg="2"
                :label="$t('attributes.customerContactPerson')"
              >
                <b-form-input
                  v-model="reference.customerContact"
                  :state="errors[0] ? false : undefined"
                />
                <b-form-invalid-feedback :state="errors[0] ? false : undefined">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>

            <b-form-group
              label-cols-md="3"
              label-cols-lg="2"
              :label="$t('attributes.customerStatement')"
            >
              <MultilangInput
                :languages="availableLanguages"
                v-model="reference.customerStatement"
                v-slot="{ value, input }"
              >
                <RichTextInput :value="value" @input="input($event)" />
              </MultilangInput>
            </b-form-group>
          </b-tab>
        </ValidationObserver>

        <ValidationObserver v-slot="{ errors }">
          <b-tab
            :title="$t('masterDataTab')"
            :title-item-class="{ invalid: countErrors(errors) > 0 }"
          >
            <h4 class="mt-4">{{ $t("releaseForPublication") }}</h4>
            <b-row>
              <b-col lg="6" md="12" class="mb-3">
                <b-form-checkbox
                  v-model="reference.releasedByCustomer"
                >{{ $t("attributes.releasedByCustomer") }}</b-form-checkbox>
              </b-col>
              <b-col lg="6" md="12" class="mb-3">
                <b-form-checkbox
                  v-model="reference.readyForVisits"
                >{{ $t("attributes.readyForReferenceVisit") }}</b-form-checkbox>
              </b-col>
            </b-row>

            <h4 class="mt-3">{{ $t("visibility") }}</h4>
            <b-form-group label-cols-md="3" label-cols-lg="2" :label="$t('visibilityLevel')">
              <b-form-select v-model="reference.visibilityLevel" style="max-width:250px">
                <b-select-option value="ALL">{{ $t("roles.all") }}</b-select-option>
                <b-select-option value="EEPOS_GENERAL">{{ $t("roles.general") }}</b-select-option>
                <b-select-option value="EEPOS_INTERNAL">{{ $t("roles.internal") }}</b-select-option>
              </b-form-select>
            </b-form-group>
            <h4 class="mt-4" v-if="isAdmin && changeLogItems.length > 0">{{ $t("changeLog") }}</h4>
            <b-row v-if="isAdmin && changeLogItems.length > 0">
              <b-col>
                <b-table
                  striped
                  sticky-header
                  :fields="changeLogFields"
                  :items="changeLogItems"
                  style="max-height: 250px; cursor: default"
                ></b-table>
              </b-col>
            </b-row>
          </b-tab>
        </ValidationObserver>
        <b-tab :title="$t('files.title')" class="mt-4">
          <h4>{{ $t("files.title") }}</h4>
          <FileUpload ref="fileUpload" @file-select="onFileSelected">
            <b-table
              class="file-table"
              :items="reference.attachedFiles"
              :fields="fileUploadTableFields"
              show-empty
              :empty-text="$t('files.empty')"
            >
              <template v-slot:cell(name)="{ item }">
                {{
                item.name || "-"
                }}
              </template>
              <template v-slot:cell(type)="{ item }">
                <div class="text-center">
                  <b-icon :icon="mimeTypeToIcon(item.mimetype)" font-size="1.2em" color="grey" />
                </div>
              </template>
              <template v-slot:cell(size)="{ item }">{{ humanFileSize(item.size, true) }}</template>
              <template v-slot:cell(uploaded)="{ item }">
                <div class="text-center">
                  <b-icon v-if="item.uploaded" icon="check" color="green" font-size="1.4em" />
                  <b-spinner v-else small />
                </div>
              </template>

              <template v-slot:cell(use)="{ item }">
                <TagMultiSelect v-model="item.use" :options="useOptions" />
              </template>

              <template v-slot:cell(actions)="{ item }">
                <b-button-group size="sm">
                  <b-button
                    class="action-button mr-1"
                    variant="primary"
                    :href="fileUrl(item)"
                    target="_blank"
                  >
                    <b-icon icon="eye-fill" />
                  </b-button>
                  <b-button
                    class="action-button mr-1"
                    variant="secondary"
                    :href="fileUrl(item, true)"
                    :disabled="!item.uploaded"
                  >
                    <b-icon icon="cloud-download" />
                  </b-button>
                  <b-button
                    class="action-button"
                    variant="danger"
                    @click="onRemoveFileClick(item.id)"
                    :disabled="!item.uploaded"
                  >
                    <b-icon icon="trash-fill" />
                  </b-button>
                </b-button-group>
              </template>
            </b-table>
            <div class="text-left pb-2">
              <b-button @click="$refs.fileUpload.manualSelect()">
                <b-icon icon="cloud-upload" font-size="1em" />
                {{ $t("files.select") }}
              </b-button>
            </div>
          </FileUpload>
        </b-tab>
      </b-tabs>

      <div class="d-flex justify-content-end my-5">
        <b-overlay
          :show="busy"
          rounded
          opacity="0.6"
          spinner-small
          spinner-variant="primary"
          class="d-inline-block"
        >
          <b-button class="m-1" variant="primary" @click="save">{{ $t("save") }}</b-button>
        </b-overlay>
        <b-button
          class="m-1"
          variant="outline-secondary"
          @click="onCancelClicked"
        >{{ $t("cancel") }}</b-button>
      </div>
    </ValidationObserver>
  </b-container>
</template>

<script>
import { fetchAPI } from "@/keecloak";
import { kee } from "@/keecloak";
import FileUpload from "@/components/FileUpload";
import Rating from "@/components/Rating";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import MultilangInput from "@/components/MultilangInput.vue";
import RichTextInput from "@/components/RichTextInput.vue";
import LocationInput from "@/components/LocationInput.vue";
import TagMultiSelect from "@/components/TagMultiSelect.vue";
import { languageOptions } from "@/languages.js";
import { productFamilies } from "@/productFamilies.js";

export default {
  components: {
    Rating,
    FileUpload,
    ValidationObserver,
    ValidationProvider,
    MultilangInput,
    RichTextInput,
    LocationInput,
    TagMultiSelect,
  },
  props: {
    id: String,
  },
  data() {
    return {
      selectedLanguages: [],
      currentLanguageCode: "de",
      changeLogItems: [],
      changeLogFields: [
        { key: "userName", label: this.$t("general.userName") },
        { key: "date", label: this.$t("general.date") },
      ],
      busy: false,
      saveError: null,
      reference: {
        title: null,
        subTitle: null,
        customerName: null,
        description: null,
        location: null,
        year: new Date().getFullYear(),
        eeposContact: null,
        customerContact: null,
        productScope: null,
        productFamily: [],
        advantages: null,
        additionalInformation: null,
        clientApplication: null,
        customerStatement: null,
        releasedByCustomer: false,
        readyForVisits: false,
        visibilityLevel: "ALL",
        referenceNumber: null,
        industry: null,
        projectVolume: null,
        projectNumber: null,
        integrator: null,
      },
      editMode: false,
      attachedFiles: null,
    };
  },
  computed: {
    productFamilies() {
      return productFamilies;
    },
    detailedErrorMessage() {
      if (this.saveError == null) return [];
      return this.saveError.details.map((detail) => {
        const curFieldName = detail.path[0];
        const curLanguage = detail.path[1];
        return this.$t("errors.inputFieldsAreTooBig", {
          field: this.$t(`attributes.${curFieldName}`),
          language: curLanguage,
          maxLength: detail.maximum,
        });
      });
    },
    availableLanguages: {
      get() {
        return [
          this.$i18n.locale,
          ...this.selectedLanguages.filter((l) => l != this.$i18n.locale),
        ].sort((a, b) => {
          if (a == this.currentLanguageCode) return -1;
          if (b == this.currentLanguageCode) return 1;
          return 0;
        });
      },
      set(value) {
        this.selectedLanguages = value.filter((l) => l != this.$i18n.locale);
        if (!value.includes(this.currentLanguageCode)) {
          this.currentLanguageCode = this.$i18n.locale;
        }
      },
    },
    languageOptions() {
      return languageOptions(this.$i18n.locale);
    },
    isAdmin() {
      return kee.userInfo.roles["references"].includes("admin");
    },
    quality() {
      if (this.reference.referenceQuality != null) {
        return this.reference.referenceQuality[this.currentLanguageCode] || 0;
      } else {
        return 0;
      }
    },
    fileUploadTableFields() {
      return [
        { key: "name", label: this.$t("name") },
        { key: "type", label: this.$t("type"), thClass: "text-center" },
        {
          key: "size",
          label: this.$t("size"),
          thClass: "text-right",
          tdClass: "text-right",
        },
        { key: "uploaded", label: this.$t("uploaded"), thClass: "text-center" },
        {
          key: "use",
          label: this.$t("files.use.title"),
          thClass: "text-center",
        },
        { key: "actions", label: "" },
      ];
    },
    getYearsUpToNow() {
      let actualYear = new Date().getFullYear();
      let yearArray = [];
      for (let index = 2006; index <= actualYear; index++) {
        yearArray.push(index);
      }
      return yearArray;
    },
    useOptions() {
      return [
        "HEADER",
        "THUMBNAIL",
        "ICON",
        "GALLERY",
        "DOWNLOAD",
      ].map((opt) => ({ value: opt, text: this.$t(`files.use.${opt}`) }));
    },
  },
  methods: {
    countErrors(errorObj) {
      return Object.keys(errorObj)
        .map((key) => errorObj[key].length)
        .reduce((sum, cur) => sum + cur, 0);
    },
    generateLangState(errors) {
      if (errors == null) return {};
      if (typeof errors == "string") return { de: { state: false } };
      const props = Object.entries(errors).reduce(
        (agg, [lang, errors]) => ({
          ...agg,
          [lang]: { state: errors[0] ? false : undefined },
        }),
        {}
      );
      return props;
    },
    onCancelClicked() {
      this.$router.push("/");
    },
    setTemporaryLocation(e) {
      this.tempLocation = e;
    },
    async save() {
      if (!(await this.$refs.form.validate())) {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0;
        return;
      }
      this.saveError = null;
      this.busy = true;
      try {
        const res = await fetchAPI(
          `references/${this.editMode ? this.id : ""}`,
          {
            method: this.editMode ? "PUT" : "POST",
            body: JSON.stringify({
              ...this.reference,
              id: undefined,
              attachedFiles: this.reference.attachedFiles
                ? this.reference.attachedFiles.map((file) => ({
                    id: file.id,
                    use: file.use,
                  }))
                : [],
              released: undefined,
              created: undefined,
              lastEdit: undefined,
              editHistory: undefined,
              releasedBy: undefined,
              referenceNumber: undefined,
              referenceQuality: undefined,
            }),
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (res.ok) {
          this.$router.push("/");
        } else {
          this.saveError = await res.json();
        }
      } catch (e) {
        console.log(e);
        this.saveError = e.message;
      } finally {
        this.busy = false;
      }
    },
    async onFileSelected(file) {
      const fileInfo = {
        name: file.name,
        mimetype: file.type,
        size: file.size,
        uploaded: false,
        use: [],
      };
      if (this.reference.attachedFiles == null) {
        this.$set(this.reference, "attachedFiles", []);
      }
      this.reference.attachedFiles.push(fileInfo);

      const response = await fetch(
        `${process.env["VUE_APP_API_BASE_URL"]}/files`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${kee.token}`,
            "X-File-Name": encodeURI(file.name),
            "Content-Type": file.type,
          },
          body: file,
        }
      );
      if (!response.ok) {
        this.reference.attachedFiles = this.reference.attachedFiles.filter(
          (i) => i != fileInfo
        );
        alert("Error while uploading file");
        return;
      }
      const data = await response.json();
      fileInfo.uploaded = true;
      fileInfo.id = data.id;
    },
    onRemoveFileClick(id) {
      this.reference.attachedFiles = this.reference.attachedFiles.filter(
        (f) => f.id != id
      );
    },
    mimeTypeToIcon(type) {
      if (type.startsWith("image/")) return "image";
      if (type.startsWith("application/pdf")) return "file-earmark-text";
      if (type.startsWith("application/zip")) return "file-earmark-zip";
      else return "file-earmark";
    },
    humanFileSize(bytes, si = false) {
      let u,
        b = bytes,
        t = si ? 1000 : 1024;
      ["", si ? "k" : "K", ..."MGTPEZY"].find(
        (x) => ((u = x), (b /= t), b ** 2 < 1)
      );
      return `${u ? (t * b).toFixed(1) : bytes} ${u}${!si && u ? "i" : ""}B`;
    },
    fileUrl(file, download = false) {
      return `${process.env.VUE_APP_API_BASE_URL}/files/${file.id}?download=${download}`;
    },
  },
  async created() {
    this.currentLanguageCode = this.$i18n.locale;
    if (this.id) {
      this.editMode = true;
      const res = await fetchAPI(`references/${this.id}`, { method: "GET" });
      if (res.ok) {
        this.reference = await res.json();
        this.reference?.attachedFiles.forEach((f) => (f.uploaded = true));
        this.changeLogItems = this.reference.editHistory.map((edit) => ({
          userName: edit.userName,
          date: new Date(edit.timestamp).toLocaleString(this.$i18n.locale),
        }));
      } else {
        throw Error(res);
      }
    }
  },
};
</script>

<style>
.file-table td {
  padding: 0.5rem;
  vertical-align: middle;
}

html {
  scroll-behavior: smooth;
}

.released-information {
  margin-bottom: 0.9rem;
  font-size: 0.9em;
  color: #9a9a9a;
}

ul.nav li.nav-item.invalid a {
  color: red;
}
</style>
